import { AreaServer } from "./servers/AreaServer.js";
import { ServerGameFile } from "./ServerGameFile.js";
import { ServerAreaFile, AREA_FILE_SYSTEM_OWNER } from "./ServerAreaFile.js";
import { ServerDebugSystem } from "./debug/ServerDebugSystem.js";
import type { IServerSystem } from "./IServerSystem.js";
import { attachServerAssets } from "./assets/inject.js";
import { ServerAssetManager } from "./assets/ServerAssetManager.js";
import type { MasterAssetTransport } from "./assets/ServerAssetManager.js";
import { makeServerAssetsFacade, type ServerAssetsFacade } from "./assets/assetsFacade.js";
import { MASTER_AREA_PROTOCOLS } from "./constants.js";
import {
    Process,
    System,
    SystemMessage,
    ClientJoinedData,
    clearObj,
    GAME_FILE_SYSTEM_OWNER,
} from "@gotti/core";

type ServerMethodsApi = {
  setState<T>(state: Partial<T>): void;
  // The client-bound three are IServerSystem's own signatures — no second copy to drift.
  dispatchToAllClients: IServerSystem['dispatchToAllClients'];
  dispatchToClient: IServerSystem['dispatchToClient'];
  dispatchToClients: IServerSystem['dispatchToClients'];
  dispatchToAreas: (areaIds: string[], data: SystemMessage) => void;
  dispatchToArea: (areaId: string, data: SystemMessage) => void;
  dispatchRaw: (data: ArrayBuffer) => void;
  requestClient: (clientId: string, data: SystemMessage) => Promise<any>;
  requestArea: (areaId: string, data: SystemMessage) => Promise<any>;
  addClientToArea: (clientId: string, areaId: string | number, opts?: any) => boolean
  replaceClientArea: (clientId: string, areaId: string | number, opts?: any) => boolean
  removeClientFromArea: (clientId: string, opts?: any) => boolean,
  dispatchToMaster: (message: { type: string | number, data: any }) => void,
  requestMaster: (message: { type: string | number, data: any }) => Promise<any>,
  createArea: (type: string, opts?: { key?: string, gameId?: string, configData?: any, managed?: boolean, maxClients?: number, emptyTtlMs?: number }) => Promise<{ areaId: string }>,
  destroyArea: (areaId: string, opts?: { drain?: boolean, whenEmpty?: boolean, timeoutMs?: number, reason?: string }) => Promise<void>,
  createChannel: (channelName: string, clientIds?: string[]) => void;
  destroyChannel: (channelName: string) => void;
  addClientToChannel: (clientId: string, channelName: string) => void;
  removeClientFromChannel: (clientId: string, channelName: string) => void;
  dispatchToChannel: (channelName: string, message: SystemMessage) => void;
  requestChannel: (channelName: string, message: SystemMessage) => Promise<Array<{ clientId: string, success: boolean, response: any, error?: string }>>;
  getClientsInChannel: (channelName: string) => Array<string>;
}

/**
 * Names of dispatch methods injected at runtime by `ServerProcess`.
 * These are `declare`-only in `ServerSystem` — any concrete subclass that
 * provides its own implementation is an error.
 */
const SERVER_DISPATCH_METHODS: ReadonlyArray<keyof ServerMethodsApi> = [
  'dispatchToAllClients', 'dispatchToClients', 'dispatchToClient',
  'dispatchToAreas', 'dispatchToArea', 'dispatchRaw',
  'requestArea', 'requestClient', 'requestMaster',
  'addClientToArea', 'replaceClientArea', 'removeClientFromArea',
  'dispatchToMaster', 'createArea', 'destroyArea', 'setState',
  'createChannel', 'destroyChannel', 'addClientToChannel',
  'removeClientFromChannel', 'dispatchToChannel', 'requestChannel',
  'getClientsInChannel',
];

export function bindServerMethods(
  obj1: ServerMethodsApi,
  obj2: Partial<ServerMethodsApi>
) {
  // ── Bind dispatch methods from the AreaServer onto the target ──────
  obj2.dispatchToAllClients = obj1.dispatchToAllClients.bind(obj1);
  obj2.dispatchToClients = obj1.dispatchToClients.bind(obj1);
  obj2.dispatchToClient = obj1.dispatchToClient.bind(obj1);
  obj2.dispatchToAreas = obj1.dispatchToAreas.bind(obj1);
  obj2.dispatchToArea = obj1.dispatchToArea.bind(obj1);
  obj2.dispatchRaw = obj1.dispatchRaw.bind(obj1);
  obj2.requestArea = obj1.requestArea.bind(obj1);
  obj2.requestClient = obj1.requestClient.bind(obj1);
  obj2.addClientToArea = obj1.addClientToArea.bind(obj1);
  obj2.replaceClientArea = obj1.replaceClientArea.bind(obj1);
  obj2.removeClientFromArea = obj1.removeClientFromArea.bind(obj1);
  obj2.dispatchToMaster = obj1.dispatchToMaster.bind(obj1);
  obj2.requestMaster = obj1.requestMaster.bind(obj1);
  obj2.createArea = obj1.createArea.bind(obj1);
  obj2.destroyArea = obj1.destroyArea.bind(obj1);
  obj2.setState = obj1.setState.bind(obj1);
  obj2.createChannel = obj1.createChannel.bind(obj1);
  obj2.destroyChannel = obj1.destroyChannel.bind(obj1);
  obj2.addClientToChannel = obj1.addClientToChannel.bind(obj1);
  obj2.removeClientFromChannel = obj1.removeClientFromChannel.bind(obj1);
  obj2.dispatchToChannel = obj1.dispatchToChannel.bind(obj1);
  obj2.requestChannel = obj1.requestChannel.bind(obj1);
  obj2.getClientsInChannel = obj1.getClientsInChannel.bind(obj1);
}

const s2nano = 1e9;
const nano2s = 1 / s2nano; // avoid a divide later, although maybe not nessecary
const ms2nano = 1e6;

/** Union of two preload lists, namespace by namespace. */
export function mergePreloads(
  a?: { client?: string[]; server?: string[] },
  b?: { client?: string[]; server?: string[] }
): { client?: string[]; server?: string[] } {
  return {
    client: [...(a?.client ?? []), ...(b?.client ?? [])],
    server: [...(a?.server ?? []), ...(b?.server ?? [])],
  };
}

export type ProcessConfig = {
    name: string,
    systems:  Array<System & IServerSystem>
}

export type PeerAreaData = {
    id: string | number,
    type: string | number,
    configData?: any,
}


export type ServerProcessStartOptions = {
  // Legacy ms-per-tick option; if provided and timeStepSec not set, it is used.
  tickRate?: number;
  // Fixed timestep size in seconds. If provided, overrides tickRate.
  timeStepSec?: number;
  // If true, run a fixed-timestep accumulator loop; otherwise variable timestep.
  fixedTimeStep?: boolean;
}

export class ServerProcess extends Process {
    readonly $areaData : any = {};
    readonly clients : {[clientId: string]: { auth: any, options: any, id: number | string } } = {};
    declare readonly systemLookup : {[system: string]: System & IServerSystem };
    declare readonly systemArray : Array<System & IServerSystem> ;
    public readonly rawClientMessageSystems: (System & IServerSystem)[] = [];
    
    private areaServer!: AreaServer

    // Lazily built on the first system add (see onAddedSystem) and shared by
    // every system on this process. The master owns the bytes: writes relay
    // to it over `areaServer.requestMaster` (ASSET_WRITE) and cold reads fetch
    // from it (ASSET_READ); the fan-out it sends back on a write (ASSET_CHANGED,
    // received by `areaServer.onAssetChange`) is what actually applies the
    // change + fires hooks — see ServerAssetManager's class doc.
    private assetManager?: ServerAssetManager;
    // The `$assets` facade handed to game/area `data` + `api` factories. Built
    // lazily over the one shared asset manager and memoized (one per process).
    private assetsFacade?: ServerAssetsFacade;
    private readonly assetUnsubscribers = new WeakMap<System, () => void>();
    /** Instances `onAddedSystem` has bound dispatch methods + accessors onto (once per instance). */
    private readonly wiredSystems = new WeakSet<System>();

    private targetTs: number = 0;
    private prevTs : number = 0;
    private longwaitMs : number = 0;
    private longwaitNano : number = 0;
    private tickLengthNano : number = 0;
  private fixedTimeStep: boolean = false;
  private stepNano: number = 0; // for fixed-step: nanos per simulation step
  private accumulatorNano: number = 0;
  /**
   * Which run of the loop is live. `onStopLoop` moves it on, and every queued tick carries the run it
   * belongs to, so a stopped run — stopped from outside, from inside its own tick, or before a restart —
   * never ticks or re-schedules again.
   */
  private loopRun: number = 0;

    // Initialize required properties with proper types
    public dispatchToAreas!: (areaIds: string[], data: SystemMessage) => void;
    public dispatchToArea!: (areaId: string, data: SystemMessage) => void;
    public dispatchToClient!: IServerSystem['dispatchToClient'];
    public dispatchToClients!: IServerSystem['dispatchToClients'];
    public dispatchToAllClients!: IServerSystem['dispatchToAllClients'];
  public dispatchRaw!: (data: ArrayBuffer) => void;

    public requestArea!: (areaId: string, data: SystemMessage) => Promise<any>;
    public requestClient!: (clientId: string, data: SystemMessage) => Promise<any>;
    public dispatchToMaster!: (message: { type: string | number, data: any }) => void;
    public requestMaster!: (message: { type: string | number, data: any }) => Promise<any>;
    public createArea!: (type: string, opts?: { key?: string, gameId?: string, configData?: any, managed?: boolean, maxClients?: number, emptyTtlMs?: number }) => Promise<{ areaId: string }>;
    public destroyArea!: (areaId: string, opts?: { drain?: boolean, whenEmpty?: boolean, timeoutMs?: number, reason?: string }) => Promise<void>;
    public addClientToArea!: (clientId: string, areaId: string | number, opts?: any) => boolean;
    public replaceClientArea!: (clientId: string, areaId: string | number, opts?: any) => boolean;
    public removeClientFromArea!: (clientId: string, opts?: any) => boolean;
    public createChannel!: (channelName: string, clientIds?: string[]) => void;
    public destroyChannel!: (channelName: string) => void;
    public addClientToChannel!: (clientId: string, channelName: string) => void;
    public removeClientFromChannel!: (clientId: string, channelName: string) => void;
    public dispatchToChannel!: (channelName: string, message: SystemMessage) => void;
    public requestChannel!: (channelName: string, message: SystemMessage) => Promise<Array<{ clientId: string, success: boolean, response: any, error?: string }>>;
    public getClientsInChannel!: (channelName: string) => Array<string>;

    constructor() {
      super();
      this.gameLoop = this.gameLoop.bind(this);
    }

    public decorate(system: System): System & IServerSystem {
      // Always inject missing ServerSystem instance state in place.
      // Dispatch methods are bound later in onAddedSystem via bindServerMethods,
      // so we only need the identity flag and instance-level state here.
      if (!('__is_server' in system)) {
        Object.defineProperty(system, '__is_server', { value: true, writable: false, configurable: false, enumerable: false });
      }
      if (!('requestCbs' in system)) {
        Object.defineProperty(system, 'requestCbs', { value: {}, writable: true, configurable: true, enumerable: true });
      }
      if (!('meta' in system)) {
        Object.defineProperty(system, 'meta', {
          value: { areaId: '', areaType: '', gameId: '', gameType: '', areaConfig: null, gameConfig: null },
          writable: true, configurable: true, enumerable: true,
        });
      }

      return system as unknown as System & IServerSystem;
    }

    public setAreaData(data: any) {
        clearObj(this.$areaData);
        for(const key in data) {
            this.$areaData[key] = data[key];
        }
    }
    public updateAreaData(data: any) {
        for(const key in data) {
            this.$areaData[key] = data[key];
        }
    }

    /**
     * Idempotently bind this process to its AreaServer: set `areaServer` and
     * bind the dispatch methods. Called both by `AreaServer.onAfterInit` (early,
     * so `ensureAssetManager`/`preloadAssets` can run over `requestMaster`
     * before the data factories) and by `initialize` (the normal boot). A
     * repeat call with the same server is a no-op — `bindServerMethods` is
     * harmless to re-run but skipping it keeps "exactly once" honest.
     */
    public attachAreaServer(server: AreaServer) {
        if (this.areaServer === server) return;
        this.areaServer = server;
        bindServerMethods(server, this);
    }

    public async initialize(server: AreaServer, gameFile?: ServerGameFile, areaFile?: ServerAreaFile, options?: ServerProcessStartOptions) {
        this.attachAreaServer(server);
        if(gameFile || areaFile) {
            await this.startGame(gameFile, areaFile, {
              ...server.gameConfigData,
              ...server.areaConfigData,
              ...options,
            });
        }
    }

  public async startGame(gameFile?: ServerGameFile, areaFile?: ServerAreaFile, options?: ServerProcessStartOptions, envOptions?: any) {
        if(this.started) {
            throw new Error(`Game already started`);
        }
        
        // Enable debug system if requested in game or area file
        if (gameFile?.debug || areaFile?.debug) {
          this.enableDebugSystem(ServerDebugSystem);
        }
        
        this._initAreaFileHandlers(areaFile);
        // NOTE: asset preload now runs earlier — in AreaServer.onAfterInit,
        // BEFORE the data + api factories — so factories (below) and systems can
        // read preloaded assets synchronously via $assets. (Previously preloaded
        // here, between the api factory and systems.)
        await this._initGameFileApis(gameFile, areaFile);
        this._initGameFileSystems(gameFile, areaFile);
        this.started = true;
    // Determine loop mode and tick length
    const useFixed = options?.fixedTimeStep === true;
    this.fixedTimeStep = useFixed;
    if (useFixed) {
      const stepSec = (options?.timeStepSec !== undefined && options?.timeStepSec !== null)
        ? options.timeStepSec
        : (gameFile?.timeStep ?? areaFile?.timeStep ?? (1/30));
      // Convert to nanos
      this.stepNano = Math.max(0, stepSec) * s2nano;
      // For scheduling, still use long-wait targeting around the step size
      this.startLoop((stepSec || (1/30)) * 1000);
    } else {
      // variable: prefer explicit timeStepSec or legacy tickRate (ms)
      if (options?.timeStepSec !== undefined) {
        this.startLoop(options.timeStepSec * 1000);
      } else if (gameFile?.timeStep !== undefined || areaFile?.timeStep !== undefined) {
        const ts = (gameFile?.timeStep ?? areaFile?.timeStep)!;
        this.startLoop(ts * 1000);
      } else if (options?.tickRate) {
        this.startLoop(options.tickRate);
      } else {
        this.startLoop(undefined);
      }
    }
    }

    private _initAreaFileHandlers(areaFile?: ServerAreaFile) {
        if(areaFile?.onJoinRequest) {
            this.onJoinRequest = areaFile.onJoinRequest.bind(this);
        }
    }

    private async _initGameFileApis(gameFile?: ServerGameFile, areaFile?: ServerAreaFile)  {
        if(gameFile?.api) {
            this.addApiFromFile(gameFile.api, this.areaServer.gameServerData, {
              game: this.areaServer.gameConfigData,
              area: this.areaServer.areaConfigData,
              $assets: this.getAssetsFacade(),
            });
        }
        if(areaFile?.api) {
            this.addApiFromFile(areaFile.api, this.areaServer.areaServerData, {
              game: this.areaServer.gameConfigData,
              area: this.areaServer.areaConfigData,
              $assets: this.getAssetsFacade(),
            });
        }
    }

    private _initGameFileSystems(gameFile?: ServerGameFile, areaFile?: ServerAreaFile) : Array<System & IServerSystem> {
        if(!this.areaServer) throw new Error(`Must call initialize with an area server before starting.`);
        const finalArray : (System & IServerSystem)[] = [];
        const filesArray = [gameFile, areaFile];
        if(areaFile) {
         // this.logger.info('initializing game file systems for areaFile: ', areaFile.type, " systems length: ", areaFile.systems.length);
        }
        filesArray.forEach(file => {
          if(!file?.systems?.length) return;
          // Owner keys say which file a running system came from — what an area
          // file reload diffs against (AreaServer.reloadAreaFile).
          const owner = file === areaFile ? AREA_FILE_SYSTEM_OWNER : GAME_FILE_SYSTEM_OWNER;
          const systems = this.addSystemsArrayFromFile(file.systems, owner) as unknown as (System & IServerSystem)[];
          finalArray.push(...systems);
        });
        return finalArray.map(f => {
          return this.decorate(f as System);
        }) as (System & IServerSystem)[];
    }

    /**
     * Lazily builds (and thereafter returns the shared) `ServerAssetManager`,
     * wiring its `MasterAssetTransport` over `areaServer.requestMaster`
     * (ASSET_WRITE/ASSET_READ) and registering `areaServer.onAssetChange` so
     * the master's fan-out (ASSET_CHANGED) applies to this process's cache
     * and fires author hooks. Safe to call multiple times/from multiple
     * call sites (onAddedSystem, preloadAssets) — built once.
     */
    public ensureAssetManager(): ServerAssetManager {
      if (!this.assetManager) {
        const transport: MasterAssetTransport = {
          write: (op, p) => this.areaServer.requestMaster({
            type: MASTER_AREA_PROTOCOLS.ASSET_WRITE,
            data: { op, namespace: p.namespace, path: p.path, bytesB64: p.bytes ? Buffer.from(p.bytes).toString('base64') : undefined },
          }).then(() => undefined),
          read: async (p) => {
            const r = await this.areaServer.requestMaster({ type: MASTER_AREA_PROTOCOLS.ASSET_READ, data: { namespace: p.namespace, path: p.path } });
            return r ? { bytes: new Uint8Array(Buffer.from(r.bytesB64, 'base64')), mimeType: r.mimeType } : null;
          },
        };
        this.assetManager = new ServerAssetManager(transport);
        // Fan-out changes from the master land here → cache coherence + hooks.
        this.areaServer.onAssetChange = (action, change) => this.assetManager!.applyRemoteChange(action, change);
      }
      return this.assetManager;
    }

    /**
     * The `$assets` facade handed to game/area `data` + `api` factories so they
     * can read preloaded assets synchronously (`$assets.server/client.getJSON`).
     * Built lazily over the one shared asset manager (`ensureAssetManager`) and
     * memoized — one facade per process, not one per call — so factories and
     * systems share a single cache. The server facade exposes write scopes too,
     * but factories should treat it as read/preloaded.
     */
    public getAssetsFacade(): ServerAssetsFacade {
      if (!this.assetsFacade) this.assetsFacade = makeServerAssetsFacade(this.ensureAssetManager());
      return this.assetsFacade;
    }

    /**
     * Load each namespaced asset path into the shared manager's cache BEFORE
     * a game/area file's systems are added, so `onInit` can use the
     * synchronous `$assets.client/server.getJSON(...)` getters without
     * awaiting. No-op when both lists are empty/undefined (asset-less games
     * are unaffected — the asset manager is not even built). Fails loud — a
     * preload that can't load throws immediately rather than leaving systems
     * to discover a silently-missing asset later.
     */
    public async preloadAssets(preloads?: { client?: string[]; server?: string[] }): Promise<void> {
      const clientPaths = preloads?.client ?? [];
      const serverPaths = preloads?.server ?? [];
      if (!clientPaths.length && !serverPaths.length) return;
      const mgr = this.ensureAssetManager();
      const load = (ns: 'client' | 'server', p: string) => mgr.loadRaw(`${ns}/${p}`).catch((e: unknown) => {
        throw new Error(`Asset preload failed for "${ns}/${p}": ${e instanceof Error ? e.message : String(e)}`);
      });
      await Promise.all([
        ...clientPaths.map((p) => load('client', p)),
        ...serverPaths.map((p) => load('server', p)),
      ]);
    }

    public addMetaToSystem(obj: any) {
        if('gameId' in this.areaServer.config.options) {
            Object.defineProperty(obj, 'gameId', {
                value: this.areaServer.config.options.gameId,
                writable: false
            });
            Object.defineProperty(obj, 'gameType', {
                value: this.areaServer.config.options.gameFile.type,
                writable: false
            });

            Object.defineProperty(obj, 'gameConfig', {
                value: this.areaServer.gameConfigData,
                writable: false
            });
        }

        Object.defineProperty(obj, 'areaId', {
            value: this.areaServer.id,
            writable: false
        });
        Object.defineProperty(obj, 'areaType', {
            value: this.areaServer.config.options.areaFile.type,
            writable: false
        });
        Object.defineProperty(obj, 'areaConfig', {
            value: this.areaServer.areaConfigData,
            writable: false
        });
    }

    public dispatchGlobalAreaAdded(areaId: string, type: string | number, configData?: any) {
        this.systemArray.forEach(s => {
            s.onGlobalAreaAdded?.(areaId, type, configData);
        });
    }

    public dispatchGlobalAreaRemoved(areaId: string, type: string | number, configData?: any) {
        this.systemArray.forEach(s => {
            s.onGlobalAreaRemoved?.(areaId, type, configData);
        });
    }

    public dispatchGameAreaAdded(areaId: string, type: string | number, configData?: any) {
        this.systemArray.forEach(s => {
            s.onGameAreaAdded?.(areaId, type, configData);
        });
    }

    public dispatchGameAreaRemoved(areaId: string, type: string | number, configData?: any) {
        this.systemArray.forEach(s => {
            s.onGameAreaRemoved?.(areaId, type, configData);
        });
    }

    public setState<T>(data: Partial<T>) {
      this.areaServer?.setState(data);
    }

    public onAddedSystem(system: System & IServerSystem) {
      if(!this.areaServer) throw new Error('Can not add systems until server is added to process.');

      // Binding is once per INSTANCE: a rolled-back restart puts the previous
      // instance back through `_addSystem`, and its dispatch methods are then its
      // own (bound) properties and its `$areaState`/meta accessors are already
      // defined (non-configurable — redefining throws).
      if (!this.wiredSystems.has(system)) {
        // ── Runtime guard: detect accidental dispatch overrides ─────────
        // `declare` emits no JS, so if a dispatch method is a real function
        // on the system at this point, the developer wrote their own body.
        for (const name of SERVER_DISPATCH_METHODS) {
          if (typeof (system as any)[name] === 'function') {
            const systemName = (system as any).name ?? '(unknown)';
            throw new Error(
              `Server system "${systemName}" defines its own "${name}" method. ` +
              `This method is injected at runtime by ServerProcess and must not be overridden. ` +
              `Remove the method body and use \`declare ${name}: IServerSystem['${name}']\` instead.`
            );
          }
        }

        bindServerMethods(this.areaServer, system)
        this.addMetaToSystem(system.meta);
        Object.defineProperty(system, '$areaState', {
            get: () => this.areaServer.areaState.data,
            set: () => { throw new Error(`Can not set $areaState on system...Use this.setState`); }
        })
        Object.defineProperty(system, '$gameAreas', {
            get: () => this.areaServer.gameAreas,
            set: () => { throw new Error(`Can not set $gameAreas on system...`); }
        });
        Object.defineProperty(system, '$areaData', {
            get: () => this.areaServer.areaServerData,
            set: () => { throw new Error(`Can not set $areaData on system...`); }
        });
        this.wiredSystems.add(system);
      }
      // What `releaseSystem` takes away again — re-attached on every add.
      this.assetUnsubscribers.get(system)?.();
      this.assetUnsubscribers.set(system, attachServerAssets(system, this.ensureAssetManager()));
      if (typeof system.onRawClientMessage === 'function' && !this.rawClientMessageSystems.includes(system)) {
        this.rawClientMessageSystems.push(system as System & IServerSystem);
      }
    }

    /**
     * A system instance left this process for good (removed, replaced by a
     * restart, or a failed replacement rolled back): drop its asset-change
     * subscription and its raw-client-message slot so it never receives
     * `onAssetUpdated`/`onAssetRemoved`/`onRawClientMessage` again.
     */
    protected override releaseSystem(system: System) {
      this.assetUnsubscribers.get(system)?.();
      this.assetUnsubscribers.delete(system);
      const idx = this.rawClientMessageSystems.indexOf(system as System & IServerSystem);
      if (idx > -1) {
        this.rawClientMessageSystems.splice(idx, 1);
      }
    }

    
  /**
   * The step, in seconds, this process's own loop updates by when it runs fixed-step (`fixedTimeStep: true`
   * on the area or game file): one `update(fixedStepSec)` per whole step. `null` when the loop is
   * variable-step or the game has not started.
   */
  public get fixedStepSec(): number | null {
    return this.fixedTimeStep ? this.stepNano * nano2s : null;
  }

  private gameLoop(run = this.loopRun) {
    // A tick queued by a run that stopLoop() (or a restart) has ended since: it must not run.
    if (run !== this.loopRun) return;
    const now = getNano();
    if (now >= this.targetTs) {
      const delta = now - this.prevTs;
      this.prevTs = now;
      this.targetTs = now + this.tickLengthNano;
      try {
        if (this.fixedTimeStep) {
          // Accumulate elapsed time and step in fixed increments
          this.accumulatorNano += delta;
          // Cap accumulator to avoid spiral of death in heavy load
          const maxFrameCatchup = 250e6; // 0.25s cap
          if (this.accumulatorNano > maxFrameCatchup) this.accumulatorNano = maxFrameCatchup;
          while (this.accumulatorNano >= this.stepNano) {
            this.update(this.stepNano * nano2s);
            this.accumulatorNano -= this.stepNano;
          }
        } else {
          // Variable timestep: pass real delta seconds
          this.update(delta * nano2s);
        }
      } catch (err) {
        // A throw escaping update() would skip the reschedule below and kill
        // this process's loop permanently and silently (server-side NPCs/state
        // freeze while clients keep running) — one bad tick from one system
        // must not do that.
        console.error('[gotti] error in system update — game loop continues', err);
      }
    }

        // do not go on to renew loop if no longer active — the game stopped, or this run ended
        // during the tick (a system called stopLoop(), or restarted the loop)
        if(!this.started || run !== this.loopRun) {
            return;
        }

        // re-grab the current time in case we ran update and it took a long time
        const remainingInTick = this.targetTs - getNano();
        const next = () => this.gameLoop(run);
        if (remainingInTick > this.longwaitNano) {
            // unfortunately it seems our code/node leaks memory if setTimeout is
            // called with a value less than 16, so we give up our accuracy for
            // memory stability
            setTimeout(next, Math.max(this.longwaitMs, 16));
        } else {
            setImmediate(next);
        }
    }

  protected onStartLoop(tickLengthMs = 1000/30) {
        this.tickLengthNano = tickLengthMs * ms2nano;
        this.prevTs = getNano();
        this.targetTs = getNano();
    this.accumulatorNano = 0;
        // We pick the floor of `tickLengthMs - 1` because the `setImmediate` below runs
        // around 16ms later and if our coarse-grained 'long wait' is too long, we tend
        // to miss our target framerate by a little bit
        this.longwaitMs = Math.floor(tickLengthMs - 1);
        this.longwaitNano = this.longwaitMs * ms2nano;
        this.gameLoop();
    }

    /** Ends the running loop: its queued tick, and any it would schedule, never runs (see `loopRun`). */
    public onStopLoop() {
        this.loopRun += 1;
    }
    public addClient(data: ClientJoinedData) {
      const { auth, options, id } = data;
      this.clients[id] = { auth, options, id };
      for(let i = 0; i < this.systemArray.length; i++) {
        const s = this.systemArray[i];
        try {
          s.onClientJoined?.(data)
        } catch (e) {
          console.error(`Error in system ${s.name} onClientJoined for client ${data.id}:`, e);
          throw e;
        }
      }
    }
    public removeClient(clientId: string, opts?: any) {
        if(clientId in this.clients) {
            this.systemArray.forEach(s => {
                s.onClientLeft?.(clientId, opts)
            });
            delete this.clients[clientId];
        }
    }

    public onJoinRequest?(server: AreaServer, clientData: ClientJoinedData, areaData?: any)
}


function getNano() {
    var hrtime = process.hrtime();
    return (+hrtime[0]) * s2nano + (+hrtime[1]);
}